import { writable, get } from 'svelte/store';
import * as pdfjs from 'pdfjs-dist';
import type { PDFDocumentProxy, PDFPageProxy } from 'pdfjs-dist';
import type { DocumentStore, DocumentState, PdfSource, PdfLoadOptions, BlobWithPage } from '../types';

// PDF.js Worker 설정 (싱글톤)
if (pdfjs.GlobalWorkerOptions && !pdfjs.GlobalWorkerOptions.workerSrc) {
    pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.js', import.meta.url).toString();
}

const CHUNK_SIZE = 131072; // 128KB 청크

/**
 * Range request 지원 여부 검증
 */
const validateRangeSupport = async (url: string): Promise<boolean> => {
    try {
        if (url.startsWith('blob:')) {
            return false;
        }

        const testReq = await fetch(url, { method: 'HEAD' });
        return testReq.headers.get('Accept-Ranges') === 'bytes' && testReq.headers.get('Content-Type') === 'application/pdf';
    } catch {
        return false;
    }
};

/**
 * PDF 문서 로드
 */
const loadPdf = async (url: string, options: PdfLoadOptions): Promise<PDFDocumentProxy> => {
    const { cMapUrl, enableRangeRequests = true, chunkSize = CHUNK_SIZE } = options;

    const supportsRange = enableRangeRequests ? await validateRangeSupport(url) : false;

    return await pdfjs.getDocument({
        url,
        cMapUrl,
        cMapPacked: true,
        rangeChunkSize: chunkSize,
        disableAutoFetch: !supportsRange,
        disableStream: false,
        httpHeaders: supportsRange
            ? {
                  Range: 'bytes=0-',
                  'Accept-Encoding': 'gzip, deflate, br',
              }
            : undefined,
    }).promise;
};

/**
 * PDF Source를 URL로 변환
 */
const pdfSourceToUrl = (source: PdfSource): { url: string; cleanup?: () => void } => {
    if (typeof source === 'string') {
        return { url: source };
    }

    if (source instanceof Blob) {
        const url = URL.createObjectURL(source);
        return { url, cleanup: () => URL.revokeObjectURL(url) };
    }

    // BlobWithPage
    const blobWithPage = source as BlobWithPage;
    if (blobWithPage.blob) {
        const url = URL.createObjectURL(blobWithPage.blob);
        return { url, cleanup: () => URL.revokeObjectURL(url) };
    }

    throw new Error('Invalid PDF source');
};

/**
 * Document Store 생성
 */
export const createDocumentStore = (): DocumentStore => {
    const { subscribe, set, update } = writable<DocumentState>({
        document: null,
        totalPages: 0,
        loading: false,
        error: null,
    });

    let cleanup: (() => void) | undefined;

    return {
        subscribe,

        async loadDocument(source: PdfSource, options: PdfLoadOptions): Promise<void> {
            update(state => ({ ...state, loading: true, error: null }));

            try {
                const { url, cleanup: urlCleanup } = pdfSourceToUrl(source);
                cleanup = urlCleanup;

                const document = await loadPdf(url, options);

                set({
                    document,
                    totalPages: document.numPages,
                    loading: false,
                    error: null,
                });
            } catch (error) {
                const errorMessage = error instanceof Error ? error : new Error('Failed to load PDF');
                console.error('PDF 로드 실패:', errorMessage);

                set({
                    document: null,
                    totalPages: 0,
                    loading: false,
                    error: errorMessage,
                });

                throw errorMessage;
            }
        },

        async getPage(pageNum: number): Promise<PDFPageProxy> {
            const state = get({ subscribe });

            if (!state.document) {
                throw new Error('Document not loaded');
            }

            if (pageNum < 1 || pageNum > state.totalPages) {
                throw new Error(`Invalid page number: ${pageNum}`);
            }

            return await state.document.getPage(pageNum);
        },

        destroy(): void {
            const state = get({ subscribe });

            state.document?.destroy();
            cleanup?.();

            set({
                document: null,
                totalPages: 0,
                loading: false,
                error: null,
            });
        },
    };
};
